import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  Menu,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import {
  exportAccountGeneralReportToCSV,
  exportAccountGeneralReportToPDF,
} from "../../utils/exportAccountGeneralReport";

const AccountGeneralReport = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [accountId, setAccountId] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [search, setSearch] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const { data: accountsData } = useFetchObjects(
    ["report-accounts"],
    "shop/accounts/?page_size=all",
    token
  );
  const { data: txData } = useFetchObjects(
    ["report-account-transactions"],
    "shop/account-transactions/?page_size=all",
    token
  );
  const { data: transferData } = useFetchObjects(
    ["report-account-transfers"],
    "shop/account-transfers/?page_size=all",
    token
  );

  const accounts = accountsData?.results || accountsData || [];
  const transactions = txData?.results || txData || [];
  const transfers = transferData?.results || transferData || [];

  const filteredBalances = useMemo(() => {
    return accounts.filter((a) => {
      if (accountId && String(a.id) !== String(accountId)) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        a.name?.toLowerCase().includes(q) ||
        a.currency_title?.toLowerCase().includes(q) ||
        a.shop_name?.toLowerCase().includes(q)
      );
    });
  }, [accounts, accountId, search]);

  const filteredTransactions = useMemo(() => {
    return transactions.filter((row) => {
      if (accountId && String(row.account) !== String(accountId)) return false;
      if (startDate && dayjs(row.transaction_date).isBefore(startDate, "day")) return false;
      if (endDate && dayjs(row.transaction_date).isAfter(endDate, "day")) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        row.account_name?.toLowerCase().includes(q) ||
        row.description?.toLowerCase().includes(q) ||
        row.transaction_type?.toLowerCase().includes(q)
      );
    });
  }, [transactions, accountId, startDate, endDate, search]);

  const filteredTransfers = useMemo(() => {
    return transfers.filter((row) => {
      if (
        accountId &&
        String(row.from_account) !== String(accountId) &&
        String(row.to_account) !== String(accountId)
      ) {
        return false;
      }
      if (startDate && dayjs(row.transfer_date).isBefore(startDate, "day")) return false;
      if (endDate && dayjs(row.transfer_date).isAfter(endDate, "day")) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        row.from_account_name?.toLowerCase().includes(q) ||
        row.to_account_name?.toLowerCase().includes(q) ||
        row.reference?.toLowerCase().includes(q) ||
        row.note?.toLowerCase().includes(q)
      );
    });
  }, [transfers, accountId, startDate, endDate, search]);

  const totalBalance = useMemo(
    () =>
      filteredBalances.reduce((sum, a) => sum + (parseFloat(a.balance) || 0), 0),
    [filteredBalances]
  );

  const handleExport = (type) => {
    const payload = {
      balances: filteredBalances,
      transactions: filteredTransactions,
      transfers: filteredTransfers,
    };
    if (type === "pdf") exportAccountGeneralReportToPDF(payload);
    if (type === "csv") exportAccountGeneralReportToCSV(payload);
    setAnchorEl(null);
  };

  return (
    <Box>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <Select
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                displayEmpty
              >
                <MenuItem value="">{t("reports.accountGeneral_allAccounts", "All Accounts")}</MenuItem>
                {accounts.map((a) => (
                  <MenuItem key={a.id} value={String(a.id)}>
                    {a.name} ({a.currency_title})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <DatePicker
              label={t("reports.datepicker_start")}
              value={startDate}
              onChange={setStartDate}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <DatePicker
              label={t("reports.datepicker_end")}
              value={endDate}
              onChange={setEndDate}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              label={t("reports.accountGeneral_search", "Search")}
            />
          </Grid>
        </Grid>
      </LocalizationProvider>

      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6">
          {t("reports.accountGeneral_totalBalance", "Total Balance")}: {totalBalance.toFixed(2)}
        </Typography>
        <Box>
          <Button variant="outlined" onClick={(e) => setAnchorEl(e.currentTarget)}>
            {t("ReportIndex.btn_export", "Export")}
          </Button>
          <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
            <MenuItem onClick={() => handleExport("pdf")}>
              {t("ReportIndex.export_pdf", "Export PDF")}
            </MenuItem>
            <MenuItem onClick={() => handleExport("csv")}>
              {t("reports.export_csv", "Export CSV")}
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell colSpan={4} align="center">
                <Typography variant="h6">
                  {t("reports.accountGeneral_balances", "Account Balances")}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t("reports.accountGeneral_account", "Account")}</TableCell>
              <TableCell>{t("reports.accountGeneral_currency", "Currency")}</TableCell>
              <TableCell>{t("reports.accountGeneral_opening", "Opening")}</TableCell>
              <TableCell>{t("reports.accountGeneral_balance", "Balance")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredBalances.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.currency_title}</TableCell>
                <TableCell>{row.opening_balance}</TableCell>
                <TableCell>{row.balance}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Divider sx={{ my: 2 }} />

      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell colSpan={8} align="center">
                <Typography variant="h6">
                  {t("reports.accountGeneral_transactions", "Account Transactions")}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t("reports.table_date", "Date")}</TableCell>
              <TableCell>{t("reports.accountGeneral_account", "Account")}</TableCell>
              <TableCell>{t("reports.accountGeneral_type", "Type")}</TableCell>
              <TableCell>{t("reports.accountGeneral_amount", "Amount")}</TableCell>
              <TableCell>{t("reports.accountGeneral_currency", "Currency")}</TableCell>
              <TableCell>{t("reports.accountGeneral_converted", "Converted")}</TableCell>
              <TableCell>{t("reports.accountGeneral_accountCurrency", "Account Currency")}</TableCell>
              <TableCell>{t("reports.balancePayment_table_notes", "Notes")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTransactions.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.transaction_date}</TableCell>
                <TableCell>{row.account_name}</TableCell>
                <TableCell>{row.transaction_type}</TableCell>
                <TableCell>{row.amount}</TableCell>
                <TableCell>{row.transaction_currency_title}</TableCell>
                <TableCell>{row.converted_amount}</TableCell>
                <TableCell>{row.account_currency_title}</TableCell>
                <TableCell>{row.description || "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Divider sx={{ my: 2 }} />

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell colSpan={10} align="center">
                <Typography variant="h6">
                  {t("reports.accountGeneral_transfers", "Account Transfers")}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t("reports.table_date", "Date")}</TableCell>
              <TableCell>{t("reports.accountGeneral_fromAccount", "From Account")}</TableCell>
              <TableCell>{t("reports.accountGeneral_toAccount", "To Account")}</TableCell>
              <TableCell>{t("reports.accountGeneral_amount", "Amount")}</TableCell>
              <TableCell>{t("reports.accountGeneral_fromCurrency", "From Currency")}</TableCell>
              <TableCell>{t("reports.accountGeneral_rate", "Rate")}</TableCell>
              <TableCell>{t("reports.accountGeneral_converted", "Converted")}</TableCell>
              <TableCell>{t("reports.accountGeneral_toCurrency", "To Currency")}</TableCell>
              <TableCell>{t("reports.accountGeneral_reference", "Reference")}</TableCell>
              <TableCell>{t("reports.balancePayment_table_notes", "Notes")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTransfers.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.transfer_date}</TableCell>
                <TableCell>{row.from_account_name}</TableCell>
                <TableCell>{row.to_account_name}</TableCell>
                <TableCell>{row.amount}</TableCell>
                <TableCell>{row.from_currency_title}</TableCell>
                <TableCell>{row.exchange_rate}</TableCell>
                <TableCell>{row.converted_amount}</TableCell>
                <TableCell>{row.to_currency_title}</TableCell>
                <TableCell>{row.reference || "-"}</TableCell>
                <TableCell>{row.note || "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AccountGeneralReport;
